import React, { useMemo } from 'react';
import { AutoApplySetupFeedbackCategory } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplySetupFeedbackEvent';
import PageTransition from '../../../components/PageTransition/PageTransition';
import { Button, Divider, Flex, Heading, Text } from '@indeed/ifl-components';
import { ChevronRight, Close } from '@indeed/ifl-icons';
import useGettext from '../../../util/GettextFactory';
import { usePageFlow } from '../../../lib/pageFlow';

interface MidSetupFeedbackCategoryStepProps {
    onClose: () => void;
}

const MidSetupFeedbackCategoryStep: React.FC<MidSetupFeedbackCategoryStepProps> = ({ onClose }) => {
    const { goForward } = usePageFlow();
    const gt = useGettext();

    const categories: { id: AutoApplySetupFeedbackCategory; title: string }[] = useMemo(
        () => [
            {
                id: 'UNABLE_TO_CONTINUE',
                title: gt.gettext('Unable to continue to the next step'),
            },
            {
                id: 'RESUME_OR_COVER_LETTER',
                title: gt.gettext('Issues with my resume or cover letter'),
            },
            {
                id: 'SOMETHING_ELSE',
                title: gt.gettext('Something else'),
            },
        ],
        [gt],
    );

    const handleCategorySelect = (category: AutoApplySetupFeedbackCategory) => {
        // Advance within the feedback sub-flow (next entry, depth +1). The details
        // route is the category-parameterized sub-flow step
        // (`/mid-setup-feedback/:category`); goForward resolves it from the flow
        // definition and fills `:category` via generatePath. Current search params
        // (currentPage/setupLogUid) are preserved. See IAFE-383.
        void goForward({ params: { category } });
    };

    return (
        <PageTransition pageKey="mid-setup-feedback-category">
            <Flex
                sx={{
                    flexDirection: 'column',
                    position: 'relative',
                }}
            >
                <Flex sx={{ justifyContent: 'flex-end' }}>
                    <Button
                        variant="tertiary"
                        iconOnly
                        size="sm"
                        onClick={onClose}
                        aria-label={gt.gettext('Close')}
                    >
                        <Close aria-hidden="true" />
                    </Button>
                </Flex>

                <Flex
                    sx={{
                        margin: '0 auto',
                        inlineSize: '100%',
                        flexDirection: 'column',
                    }}
                >
                    <Flex sx={{ flexDirection: 'column' }}>
                        <Heading level={4} sx={{ marginBlockEnd: 0 }}>
                            {gt.gettext('What is the issue?')}
                        </Heading>
                        <Text level={3} variant={'secondary'}>
                            {gt.gettext(
                                'Your feedback will help us fix issues and improve your experience across Indeed.',
                            )}
                        </Text>
                    </Flex>

                    <Flex
                        sx={{
                            flexDirection: 'column',
                            gap: 0,
                        }}
                    >
                        {categories.map((cat) => (
                            <React.Fragment key={cat.id}>
                                <Button
                                    variant="text"
                                    size="lg"
                                    onClick={() => handleCategorySelect(cat.id)}
                                    sx={{
                                        justifyContent: 'space-between',
                                        paddingBlock: 5,
                                        paddingInline: 0,
                                        textAlign: 'start',
                                    }}
                                >
                                    <Text level={3} sx={{ fontWeight: 400 }}>
                                        {cat.title}
                                    </Text>
                                    <ChevronRight color={'foreground.strong'} aria-hidden="true" />
                                </Button>
                                <Divider />
                            </React.Fragment>
                        ))}
                    </Flex>
                </Flex>
            </Flex>
        </PageTransition>
    );
};

export default MidSetupFeedbackCategoryStep;
